import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Notifications.css';

/**
 * Уведомления оператору вместо `alert`.
 *
 * `alert` блокирует страницу, не различает важность и не даёт показать два
 * сообщения сразу: успешная отправка и предупреждение об отставании реестра
 * выстраивались в очередь модальных окон.
 *
 * Ошибка не гаснет сама — оператор распоряжается реальными деньгами и не
 * должен пропустить отказ, отвернувшись от экрана. Успех и подсказки гаснут.
 */
const NotificationsContext = createContext(null);

const AUTO_DISMISS_MS = 7000;

export const useNotify = () => {
  const notify = useContext(NotificationsContext);

  if (!notify) {
    throw new Error('useNotify вызван вне NotificationsProvider');
  }

  return notify;
};

export const NotificationsProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const lastId = useRef(0);

  const dismiss = useCallback((id) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (type, text) => {
      lastId.current += 1;
      const id = lastId.current;

      setItems((current) => [...current, { id, type, text }]);

      if (type !== 'error') {
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }

      return id;
    },
    [dismiss],
  );

  const notify = useMemo(
    () => ({
      success: (text) => push('success', text),
      error: (text) => push('error', text),
      info: (text) => push('info', text),
    }),
    [push],
  );

  return (
    <NotificationsContext.Provider value={notify}>
      {children}

      <div className="notifications">
        {items.map(({ id, type, text }) => (
          <div
            key={id}
            className={`notification notification--${type}`}
            role={type === 'error' ? 'alert' : 'status'}
          >
            <span className="notification__text">{text}</span>
            <button
              type="button"
              className="notification__close"
              onClick={() => dismiss(id)}
              aria-label="Закрыть уведомление"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
